import { format, parse, subDays } from 'date-fns';
import { identity } from 'lodash/fp';
import { getConfig } from './config';
import { setSalesChannel } from './utils';
import { CreateSearchInput, PlanStoreSearch, Room } from './types';
const { RESERVATIONS_SITE_URL } = getConfig(process.env);

export type BookingQueryParams = {
	hotelCode?: string | string[];
	checkIn?: string;
	checkOut?: string;
	adults?: string | string[];
	children?: string | string[];
	promoCode?: string;
	ratePlanCode?: string;
	roomOfferFilter?: string;
	showPrivateRetreats?: boolean;
};

const dateFormat = 'yyyy-MM-dd';

const stringToInt = n => parseInt(n, 10);
const add = a => b => a + b;
const generateNumbersList = (n: number, start = 1) => [...Array(n).keys()].map(add(start));
const splitTargetPropertyCodes = targetPropertyCodes => {
	if (!targetPropertyCodes) return undefined;

	return targetPropertyCodes.replace(/\[|\]|"|\s/g, '').split(',');
};
const calculateCheckInEnd = (date, params) => {
	if (!date) return undefined;
	try {
		const offset = stringToInt(params.minStay) || 1;
		return format(subDays(parse(date, dateFormat, new Date()), offset), dateFormat);
	} catch (e) {
		return e;
	}
};

const reformatDate = (date: string) => {
	if (!date) return undefined;
	try {
		const dateFormat = date.includes('/') ? 'MM/dd/yyyy' : 'yyyy-MM-dd';
		const parsedDate = parse(date, dateFormat, new Date());

		return format(parsedDate, 'yyyy-MM-dd');
	} catch (e) {
		return format(new Date(), 'yyyy-MM-dd');
	}
};
const queryParamsMap = new Map([
	['cawConfig.caEnabled', { key: 'calendarAvailabilityEnabled' }],
	['cawConfig.closingDate', { key: 'checkOutEnd' }],
	['cawConfig.cta', { key: 'ctaText' }],
	['cawConfig.hidePromoCode', { key: 'hidePromoCode' }],
	['cawConfig.maxNumberOfRooms', { key: 'maxRooms' }],
	['cawConfig.minNumberOfNights', { key: 'minStay' }],
	['cawConfig.promoByGuest', { key: 'promoByGuest' }],
	['cawConfig.startDate', { key: 'checkInStart' }],
	['cawConfig.targetPropertyCodes', { key: 'propertyList', f: splitTargetPropertyCodes }],
	['generalReservationForm.checkInDate', { key: 'checkIn', f: reformatDate }],
	['generalReservationForm.checkOutDate', { key: 'checkOut', f: reformatDate }],
	['generalReservationForm.locationId', { key: 'hotelCode' }],
	['generalReservationForm.promoCode', { key: 'promoCode' }],
	['generalReservationForm.ratePlanCode', { key: 'ratePlanCode' }],
	['generalReservationForm.roomOwsCodes', { key: 'roomOwsCodes' }],
	['hotel-code', { key: 'hotelCode' }],
	['promo-code', { key: 'promoCode' }],
	['showPrivateRetreats', { key: 'showPrivateRetreats' }],
	['channel', { key: 'channel' }],
	['ta.ppId', { key: 'ppId' }],
	['ta.ppIdType', { key: 'pullTAInfo' }],
]);
const blacklist = [
	'cawConfig.guests',
	'generalReservationForm.showAllAvailablePackages',
	'generalReservationForm.locationName',
	'locale',
	'actionStep',
	'startBookingFlow',
	'cawConfig.customBooker',
	'cawConfig.fixedAD',
	'cawConfig.showAllPkgs',
	'ta.ppQualifier',
];
const ppParams = ['ppMode', 'ppId', 'pullTAInfo', 'ppUrl'];
const tripTeaseWizardParams = ['triptease-applications', 'tt-wizard-id', 'tt-preview-cache-key'];
const ppBlackList = [
	'maxRooms',
	'checkInStart',
	'checkOutEnd',
	'minStay',
	'hidePromoCode',
	'propertyList',
	'promoByGuest',
];

const guestCountPerRoomRegex = /generalReservationForm.guestCountPerRoom\[\d+\]./g;

const shouldHandleEmployeeCode = promoCode =>
	promoCode && ['EMPCMP##', 'EMPECE##'].some(a => a === promoCode);

const handleEmployeeMode = params =>
	shouldHandleEmployeeCode(params.promoCode)
		? {
				...params,
				employeeMode: true,
			}
		: params;

const handlePreferredPartnerMode = params =>
	Boolean(params.ppMode) && Boolean(params.ppId) && Boolean(params.pullTAInfo)
		? Object.keys(params).reduce(
				(acc, param) => (ppBlackList.includes(param) ? acc : { ...acc, [param]: params[param] }),
				{}
			)
		: Object.keys(params).reduce(
				(acc, param) => (ppParams.includes(param) ? acc : { ...acc, [param]: params[param] }),
				{}
			);

const getCompleteQueryParamsMaps = (maxNoOfRooms: number) =>
	maxNoOfRooms > 0
		? generateNumbersList(maxNoOfRooms, 0).reduce((acc, roomCount) => {
				acc.set(`generalReservationForm.guestCountPerRoom[${roomCount}].adultCount`, {
					key: 'adults',
				});
				acc.set(`generalReservationForm.guestCountPerRoom[${roomCount}].childCount`, {
					key: 'children',
				});

				return acc;
			}, queryParamsMap)
		: queryParamsMap;

const handlePromoCode = params =>
	!params.promoByGuest && params.promoCode
		? {
				...params,
				[params.hidePromoCode ? 'promoHide' : 'promoDisabled']: true,
			}
		: {
				...params,
				...(params?.ppMode ? { promoHide: true } : {}),
			};

const handleCheckInEndDate = params =>
	params.checkOutEnd
		? {
				...params,
				checkInEnd: calculateCheckInEnd(params.checkOutEnd, params),
			}
		: params;

const removeBlacklisted = params =>
	Object.keys(params).reduce(
		(acc, param) => (blacklist.includes(param) ? acc : { ...acc, [param]: params[param] }),
		{}
	);

const transformer = (params, completeQueryParamsMaps) => (acc, paramKey) => {
	const { key = paramKey, f = identity } = completeQueryParamsMaps.get(paramKey) || {};

	return {
		...acc,
		[key]: Object.prototype.hasOwnProperty.call(acc, key)
			? [...(Array.isArray(acc[key]) ? acc[key] : [acc[key]]), f(params.get(paramKey), params)]
			: f(params.get(paramKey), params),
	};
};

const map = (params: URLSearchParams, completeQueryParamsMaps) =>
	Array.from(params.keys()).reduce(transformer(params, completeQueryParamsMaps), {});

export function parseBookingQueryParams(search: string): BookingQueryParams {
	//sometimes with redirects keys become double or more encoded which URLSearchParams doesn't handle
	const keys = location.search.replace('?', '').split('&');
	let newSearch = search;
	keys?.forEach(key => {
		let decoded = decodeURIComponent(key);
		while (decoded !== key) {
			newSearch = newSearch.replace(key, decoded);
			key = decoded;
			decoded = decodeURIComponent(key);
		}
	});
	window.history.replaceState(null, '', `${newSearch}${location.hash}`);
	const params = new URLSearchParams(newSearch);
	const maxNoOfRoomsArray = Array.from(params.keys())
		.filter(key => key.match(guestCountPerRoomRegex))
		.map(matched => stringToInt(matched.split(/[[\]]/)[1]) + 1);

	const completeQueryParamsMaps = getCompleteQueryParamsMaps(
		Math.max(...[0, ...maxNoOfRoomsArray])
	);

	const bookingQueryParams = removeBlacklisted(
		handleEmployeeMode(
			handlePreferredPartnerMode(
				handleCheckInEndDate(handlePromoCode(map(params, completeQueryParamsMaps)))
			)
		)
	);

	//saving Sales-Channel value if present before it's removed from the url
	setSalesChannel(bookingQueryParams['channel']);

	return bookingQueryParams;
}

export const arraysToRooms = (arrays: {
	adults?: string | string[];
	children?: string | string[];
}): Room[] => {
	try {
		let adultsParam = arrays['adults'] || '';
		adultsParam = Array.isArray(adultsParam) ? adultsParam : [adultsParam];
		let childrenParam = arrays['children'] || '';
		childrenParam = Array.isArray(childrenParam) ? childrenParam : [childrenParam];
		if (adultsParam) {
			return adultsParam.map((adults, i) => {
				try {
					return {
						adults: parseInt(adults, 10),
						children: parseInt(childrenParam[i] || '0', 10),
					};
				} catch {
					// empty by design
				}
				return { adults: 2, children: 0 };
			});
		}
	} catch {
		// empty by design
	}
	return [
		{
			adults: 2,
			children: 0,
		},
	];
};

export const queryParams = {
	startDate: 'generalReservationForm.checkInDate',
	endDate: 'generalReservationForm.checkOutDate',
	flexDatesWindow: 'flexDatesWindow',
	destination: 'generalReservationForm.locationId',
	adultsCount: [
		'generalReservationForm.guestCountPerRoom[0].adultCount',
		'generalReservationForm.guestCountPerRoom[1].adultCount',
		'generalReservationForm.guestCountPerRoom[2].adultCount',
		'generalReservationForm.guestCountPerRoom[3].adultCount',
	],
	childrenCount: [
		'generalReservationForm.guestCountPerRoom[0].childCount',
		'generalReservationForm.guestCountPerRoom[1].childCount',
		'generalReservationForm.guestCountPerRoom[2].childCount',
		'generalReservationForm.guestCountPerRoom[3].childCount',
	],
	promoCode: 'generalReservationForm.promoCode',
	roomCodes: 'generalReservationForm.roomOwsCodes',
	offerCode: 'generalReservationForm.ratePlanCode',
	customBooker: 'cawConfig.customBooker',
	promoByGuest: 'cawConfig.promoByGuest',
	internalCampaignTracking: '_s_icmp',
};

export const generateParamString = (
	planStoreSearch: PlanStoreSearch,
	availCheckTemplate: string,
	roomFindingMethod: string,
	ppMode = false
) => {
	const params = {};
	if (planStoreSearch.checkinDate && planStoreSearch.checkoutDate) {
		params[queryParams.startDate] = format(planStoreSearch.checkinDate, dateFormat);
		params[queryParams.endDate] = format(planStoreSearch.checkoutDate, dateFormat);
	}

	if (planStoreSearch.flexDatesSelected && planStoreSearch.flexDatesSelected > 0) {
		params[queryParams.flexDatesWindow] = planStoreSearch.flexDatesSelected;
	}
	if (planStoreSearch.rooms) {
		planStoreSearch.rooms.forEach((r, i) => {
			params[queryParams.adultsCount[i]] = r.adults;
			params[queryParams.childrenCount[i]] = r.children;
		});
	}

	params[queryParams.destination] = planStoreSearch.hotelCode;
	const promoCode = planStoreSearch.promoCode;
	const omitPpInternalPreferred = ppMode && promoCode && promoCode.toUpperCase() === 'PREFERRED';
	if (promoCode && !omitPpInternalPreferred) {
		params[queryParams.promoCode] = promoCode;
		params[queryParams.promoByGuest] = true;
	}

	// Need both of these checks. CAW set offerCode. ABF sets roomOfferFilter
	if (planStoreSearch.offerCode) {
		params[queryParams.offerCode] = planStoreSearch.offerCode;
		params[queryParams.internalCampaignTracking] = 'shpcart_offer_detail_check_rates';
	}

	if (planStoreSearch.roomOfferFilter) {
		params[queryParams.offerCode] = planStoreSearch.roomOfferFilter;
	}

	if (planStoreSearch.roomCode) {
		params[queryParams.roomCodes] = planStoreSearch.roomCode;
	}
	if (planStoreSearch.showPrivateRetreats) {
		params['showPrivateRetreats'] = true;
	}

	params[queryParams.customBooker] = true;
	params['availCheckTemplate'] = availCheckTemplate;
	params['roomFindingMethod'] = roomFindingMethod;
	// persist the query string campaign value, if present
	const urlParams = parseBookingQueryParams(location.search);
	params[queryParams.internalCampaignTracking] =
		urlParams[queryParams.internalCampaignTracking] || params[queryParams.internalCampaignTracking];
	//preserve Triptease params only when values are populated in the current URL
	tripTeaseWizardParams.forEach(param => {
		const value = urlParams[param];
		if (typeof value === 'string' && value.trim() !== '') {
			params[param] = value;
		}
	});
	//persist ppMode query string params
	ppParams.forEach(ppParam => {
		params[ppParam] = urlParams[ppParam];
	});
	const paramString = Object.keys(params)
		.filter(key => params[key] !== undefined)
		.map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
		.join('&');
	return `${paramString}`;
};

export const bookNowParams = {
	bookingId: 'bookingId',
	checkinDate: 'checkIn',
	checkoutDate: 'checkOut',
	hotelCode: 'hotelCode',
	adults: 'adults',
	children: 'children',
	promoCode: 'promoCode',
	roomOwsCode: 'roomOwsCode',
	roomOfferCode: 'roomOfferCode',
};

export const generateBookNowUrl = (
	searchParams: CreateSearchInput,
	bookingId: string | undefined,
	roomOwsCode: string | undefined,
	roomOfferCode: string | undefined,
	availCheckTemplate: string,
	roomFindingMethod: string
) => {
	const params = {};

	if (bookingId) {
		params[bookNowParams.bookingId] = bookingId;
	}
	if (roomOwsCode) {
		params[bookNowParams.roomOwsCode] = roomOwsCode;
	}
	if (roomOfferCode) {
		params[bookNowParams.roomOfferCode] = roomOfferCode;
	}
	params[bookNowParams.hotelCode] = searchParams.hotelCode;
	params[bookNowParams.checkinDate] = searchParams.checkinDate;
	params[bookNowParams.checkoutDate] = searchParams.checkoutDate;
	if (searchParams.rooms) {
		params[bookNowParams.adults] = searchParams.rooms.map(room => room.adults);
		params[bookNowParams.children] = searchParams.rooms.map(room => room.children);
	}
	params[bookNowParams.promoCode] = searchParams.promoCode || '';

	params['availCheckTemplate'] = availCheckTemplate;
	params['roomFindingMethod'] = roomFindingMethod;

	const paramString = Object.keys(params)
		.map(key => {
			const value = params[key];
			if (Array.isArray(value)) {
				return value
					.reduce((result, arrayValue) => {
						return [...result, `${encodeURIComponent(key)}=${encodeURIComponent(arrayValue)}`];
					}, [])
					.join('&');
			}

			return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
		})
		.join('&');
	return `${RESERVATIONS_SITE_URL}/${searchParams.locale}?${paramString}`;
};
